import { useEffect, useRef } from "react";

export default function QuemSomos() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".qs-item").forEach((item, i) => {
              setTimeout(() => item.classList.add("qs-visible"), i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="quem-somos" className="py-16 lg:py-20 bg-white overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Image */}
          <div className="qs-item opacity-0 translate-x-[-24px] transition-all duration-700 relative">
            <div className="relative rounded-2xl overflow-hidden h-[420px] lg:h-[520px]">
              <img
                src="https://storage.readdy-site.link/project_files/9309375e-f328-4fba-b8d7-64d73f28a855/b7b2c329-57c5-49b4-a440-f6db15b5d901_compressed_unnamed.webp"
                alt="Fachada da Ferrarezi, Sede em São Paulo"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </div>

            {/* Floating stat card */}
            <div className="absolute -bottom-6 -right-4 lg:-right-8 bg-[#FBC52D] rounded-2xl p-5 shadow-xl">
              <div className="text-3xl font-bold text-black">+30</div>
              <div className="text-black/70 text-sm mt-1">Anos de experiência</div>
            </div>

            {/* Decorative element */}
            <div className="absolute -top-4 -left-4 w-20 h-20 rounded-2xl border-2 border-[#FBC52D]/20 -z-10" />
          </div>

          {/* Right: Content */}
          <div className="qs-item opacity-0 translate-x-6 transition-all duration-700">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="w-8 h-px bg-black" />
              <span className="text-black text-lg font-semibold tracking-widest uppercase">
                QUEM SOMOS?
              </span>
            </div>

            <h2 className="text-[28px] md:text-[34px] lg:text-[46px] font-bold text-black leading-tight mb-6">
              Aceleradora de pessoas e resultados.
            </h2>

            <p className="text-black/70 text-lg leading-relaxed mb-6">
              A Ferrarezi nasceu para transformar a forma como as empresas cuidam de pessoas e geram resultados. Somos uma consultoria futurística e tecnológica que combina <strong>benefícios, seguros, gestão de dados e estratégia</strong> em um único ecossistema.
            </p>

            <p className="text-black/70 text-base leading-relaxed mb-10">
              Com mais de +30 anos de experiência e mais de 80 produtos disponíveis, unimos tecnologia de ponta, inteligência artificial e atendimento hiperpersonalizado para entregar resultados reais e mensuráveis: redução de custos, controle de sinistralidade, diminuição do absenteísmo, retenção de talentos e aumento de produtividade.
            </p>

            {/* Values */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: "ri-brain-line", label: "Inovação e tecnologia" },
                { icon: "ri-shake-hands-line", label: "Ética e transparência" },
                { icon: "ri-bar-chart-2-line", label: "Decisões baseadas em dados" },
                { icon: "ri-heart-line", label: "Humanização do atendimento" },
              ].map((val) => (
                <div key={val.label} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#FBC52D]/10 shrink-0">
                    <i className={`${val.icon} text-[#FBC52D] text-base`} />
                  </div>
                  <span className="text-black/80 text-sm font-medium">{val.label}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-10 pt-8 border-t border-gray-100">
              <button
                onClick={() => document.querySelector("#contato")?.scrollIntoView({ behavior: "smooth" })}
                className="whitespace-nowrap bg-[#FBC52D] text-black font-bold px-8 py-4 rounded-full hover:bg-[#d4a800] transition-all duration-200 cursor-pointer text-sm"
              >
                Fale com nossos especialistas
              </button>
              <button
                onClick={() => document.querySelector("#resultados")?.scrollIntoView({ behavior: "smooth" })}
                className="whitespace-nowrap border border-black/20 text-black font-semibold px-8 py-4 rounded-full hover:bg-black/5 hover:border-black/40 transition-all duration-200 cursor-pointer text-sm"
              >
                Ver resultados
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .qs-visible {
          opacity: 1 !important;
          transform: translateX(0) !important;
        }
      `}</style>
    </section>
  );
}